import { useGlobalMenu } from '@/contexts/menu/GlobalMenuProvider';
import { cls } from '@/utils';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { LevelTitle } from '../LevelTitle/LevelTitle';
import { ScrollButton } from '../ScrollButton/ScrollButton';
import styles from './Panel.module.scss';

interface Props {
	children: ReactNode;
	active?: boolean;
	level: 1 | 2;
	currentTitle?: string;
	setActiveLevel: (level: 1 | 2) => void;
	className?: string;
}

const vhToPx = (vh: number) => {
	return Math.round((vh / 100) * window.innerHeight);
};

export const Panel: React.FC<Props> = ({ children, active, level, currentTitle, setActiveLevel, className }) => {
	const { isMenuOpen } = useGlobalMenu();
	const [isOverflowing, setIsOverflowing] = useState(false);
	const [isAtBottom, setIsAtBottom] = useState(false);
	const [isAtTop, setIsAtTop] = useState(true);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const innerRef = useRef<HTMLDivElement | null>(null);
	const titleRef = useRef<HTMLDivElement | null>(null);
	const scrollButtonDownRef = useRef<HTMLDivElement | null>(null);
	const scrollButtonUpRef = useRef<HTMLDivElement | null>(null);
	const showScrollButton = isOverflowing && isMenuOpen && Boolean(children);
	const scrollDistanceOffset = 64;

	/**
	 * Wait for menu to expand and check if content is overflowing in Level 1
	 */
	useEffect(() => {
		const container = containerRef.current;
		let timeout: NodeJS.Timeout;

		if (container && level === 1 && isMenuOpen) {
			timeout = setTimeout(() => {
				if (innerRef.current) {
					const inner = innerRef.current;
					const isOverflowingY = inner.scrollHeight > inner.clientHeight;
					setIsOverflowing(isOverflowingY);
				}
			}, 300);
		}

		return () => {
			clearTimeout(timeout);
		};
	}, [active, children, level, isMenuOpen]);

	useEffect(() => {
		if (innerRef.current && titleRef.current && level !== 1 && isMenuOpen) {
			const inner = innerRef.current;
			const style = titleRef.current && window.getComputedStyle(titleRef.current);
			const marginBottom = style?.marginBottom ? parseFloat(style.marginBottom) : 0;
			const totalHeight = titleRef.current?.offsetHeight + marginBottom + 24 + inner.clientHeight;

			if (totalHeight <= vhToPx(70)) return;

			const isOverflowingY = inner.scrollHeight > inner.clientHeight;
			setIsOverflowing(isOverflowingY);
		}
	}, [active, children, isMenuOpen, level]);

	const handleScroll = () => {
		const container = innerRef.current;

		if (!container) return;

		const offset = 30;
		const atBottom = container.scrollHeight - container.scrollTop - offset <= container.clientHeight; // TODO: Kolla på scroll

		setIsAtBottom(atBottom);
		const atTop = container.scrollTop === 0;
		setIsAtTop(atTop);
	};

	const scrollDown = () => {
		const container = innerRef.current;
		if (!container) return;

		const buttonHeight =
			(scrollButtonUpRef?.current?.clientHeight || 0) + (scrollButtonDownRef?.current?.clientHeight || 0);

		const scrollDistance = container.scrollTop + (container.clientHeight - buttonHeight - scrollDistanceOffset);

		innerRef.current?.scrollTo({
			top: scrollDistance,
			behavior: 'smooth',
		});
	};

	const scrollUp = () => {
		const container = innerRef.current;
		if (!container) return;

		const buttonHeight =
			(scrollButtonUpRef?.current?.clientHeight || 0) + (scrollButtonDownRef?.current?.clientHeight || 0);

		const scrollDistance = container.scrollTop - (container.clientHeight - buttonHeight - scrollDistanceOffset);

		innerRef.current?.scrollTo({
			top: scrollDistance,
			behavior: 'smooth',
		});
	};

	return (
		<div
			className={cls(
				styles.panel,
				level === 1 && styles.levelOne,
				isMenuOpen && styles.open,
				(!isMenuOpen || (isMenuOpen && !active)) && styles.hide,
				className,
			)}
			tabIndex={-1}
			ref={containerRef}
		>
			{currentTitle && level !== 1 && (
				<div className={styles.titleWrapper} ref={titleRef}>
					<LevelTitle title={currentTitle} onClick={() => setActiveLevel(1)} />
				</div>
			)}

			<div className={styles.inner} ref={innerRef} onScroll={handleScroll} tabIndex={-1}>
				<ScrollButton
					onClick={scrollUp}
					direction="up"
					forwardedRef={scrollButtonUpRef}
					show={showScrollButton && !isAtTop}
				/>

				{children}

				<ScrollButton
					onClick={scrollDown}
					direction="down"
					forwardedRef={scrollButtonDownRef}
					show={showScrollButton && !isAtBottom}
				/>
			</div>
		</div>
	);
};
